<script>
import { authMethods, authComputed } from '@state/helpers';
import { loadStripe } from '@stripe/stripe-js';
import _ from 'lodash';

export default {
  page: {
    title: 'Checkout',
    meta: [{ name: 'description', content: 'The Checkout page.' }],
  },
  props: {
    plan: {
      type: Object,
      required: true,
      default: () => {},
    },
    passThroughQuery: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      name: '',
      addressLine1: '',
      addressLine2: '',
      addressState: '',
      addressCity: '',
      addressCountry: '',
      stripe: {},
      elements: {},
      card: {},
      cardExp: {},
      cardCvc: {},
      couponCode: '',
      checkingCouponCode: false,
      couponCodeError: '',
      validCoupon: {},
      authError: null,
      submittingPayment: false,
    };
  },
  computed: {
    ...authComputed,
    placeholders() {
      return {
        cardHolder: `Full Name`,
        addressLine1: `Billing Address`,
        addressLine2: `Second Line Billing Address`,
        addressCity: 'City',
        addressState: 'State',
        addressCountry: 'Country',
        couponCode: 'Coupon Code',
      };
    },
    _() {
      return _;
    },
  },
  created() {
    loadStripe(process.env.VUE_APP_STRIPE_KEY).then((result) => {
      this.stripe = result;
      this.elements = result.elements();
      this.card = this.elements.create('cardNumber', {
        style: {
          base: {
            padding: '4em',
            fontSize: '15px',
            letterSpacing: '0.761905px',
            '::placeholder': {
              color: '#979797',
              backgroundColor: '#f5f5ef',
            },
          },
        },
        placeholder: '0000 0000 0000 0000',
      });
      this.card.mount(this.$refs.card);

      this.cardExp = this.elements.create('cardExpiry', {
        style: {
          base: {
            fontSize: '15px',
            letterSpacing: '0.761905px',
            '::placeholder': {
              color: '#979797',
              backgroundColor: '#f5f5ef',
            },
          },
        },
        placeholder: 'MM/YY',
      });
      this.cardExp.mount(this.$refs.cardExp);

      this.cardCvc = this.elements.create('cardCvc', {
        style: {
          base: {
            fontSize: '15px',
            letterSpacing: '0.761905px',
            '::placeholder': {
              color: '#979797',
              backgroundColor: '#f5f5ef',
            },
          },
        },
        placeholder: '000',
      });
      this.cardCvc.mount(this.$refs.cardCvc);
    });

    if (this.$router.history.current.query.coupon) {
      this.couponCode = this.$router.history.current.query.coupon;
    }
  },
  methods: {
    ...authMethods,
    async checkCouponLocal() {
      this.couponCodeError = '';
      if (this.couponCode) {
        this.checkingCouponCode = true;
        await this.checkCoupon(this.couponCode)
          .then((response) => {
            if (response && response.data.success) {
              this.validCoupon = response.data.coupon;
            } else {
              this.couponCodeError = 'Coupon not found';
            }
            this.checkingCouponCode = false;
          })
          .catch((error) => {
            this.checkingCouponCode = false;
            this.couponCodeError = error;
          });
      } else {
        this.couponCodeError = 'Enter a coupon code';
      }
    },
    async checkoutFree() {
      await this.submitPayment(true);
    },
    discountValue(plan) {
      if (
        this.validCoupon &&
        this.validCoupon.amount_off &&
        plan.amount >= this.validCoupon.amount_off
      ) {
        return this.validCoupon.amount_off;
      } else if (
        this.validCoupon &&
        this.validCoupon.amount_off &&
        plan.amount < this.validCoupon.amount_off
      ) {
        return plan.unit_amount;
      } else if (this.validCoupon && this.validCoupon.percent_off) {
        return (plan.unit_amount * this.validCoupon.percent_off) / 100;
      } else {
        return 0;
      }
    },
    async submitPayment(isFree = false) {
      this.submittingPayment = true;

      if (this.couponCode.length > 0 && _.isEmpty(this.validCoupon)) {
        // Coupon was set without valid. Lets double check
        await this.checkCouponLocal();
      }

      if (!isFree) {
        const data = {
          name: this.name,
          address_line1: this.addressLine1,
          address_line2: this.addressLine2,
          address_state: this.addressState,
          address_city: this.addressCity,
          address_country: this.addressCountry,
        };

        await this.stripe.createToken(this.card, data).then(async (result) => {
          // Access the token with result.token
          if (result.error) {
            // Something went wrong
            this.submittingPayment = false;
            this.authError = result.error.message;
          } else {
            await this.checkout({
              trainer_id: this.currentUser.trainer._id,
              stripe_token: result.token,
              plan_id: this.plan.id,
              coupon: this.validCoupon,
            })
              .then((response) => {
                this.submittingPayment = false;
                if (response && response.data && response.data.success) {
                  this.$emit('set-checkout-completed', true);
                } else {
                  this.authError = response.data.message;
                }
              })
              .catch((error) => {
                this.submittingPayment = false;
                this.authError = error.message;
              });
          }
        });
      } else {
        await this.checkout({
          trainer_id: this.currentUser.trainer._id,
          plan_id: this.plan.id,
          coupon: this.validCoupon,
        })
          .then((response) => {
            this.submittingPayment = false;
            if (response && response.data && response.data.success) {
              this.$emit('set-checkout-completed', true);
            } else {
              this.authError = response.data.message;
            }
          })
          .catch((error) => {
            this.submittingPayment = false;
            this.authError = error.message;
          });
      }
    },
  },
};
</script>

<template>
  <div class="content-wrapper">
    <form
      v-show="plan.unit_amount > 0"
      class="payment-form"
      @submit.prevent="submitPayment(false)"
    >
      <div class="form-header">
        <h1>Checkout</h1>
      </div>
      <b-row>
        <b-col>
          <label for="name">Name On Card</label>
          <BaseInputText
            v-model="name"
            name="cardHolder"
            :placeholder="placeholders.cardHolder"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <label for="addressLine1">Billing Address</label>
          <BaseInputText
            v-model="addressLine1"
            name="addressLine1"
            :placeholder="placeholders.addressLine1"
          />
        </b-col>
        <b-col>
          <label for="addressLine2">Second Line Billing Address</label>
          <BaseInputText
            v-model="addressLine2"
            name="addressLine2"
            :placeholder="placeholders.addressLine2"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <label for="addressCity">City</label>
          <BaseInputText
            v-model="addressCity"
            name="addressCity"
            :placeholder="placeholders.addressCity"
          />
        </b-col>
        <b-col>
          <label for="addressState">State</label>
          <BaseInputText
            v-model="addressState"
            name="addressState"
            :placeholder="placeholders.addressState"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <label for="addressCountry">Country</label>
          <BaseInputText
            v-model="addressCountry"
            name="addressCountry"
            :placeholder="placeholders.addressCountry"
          />
        </b-col>
      </b-row>
      <b-row class="credit-card">
        <b-col>
          <label for="">Credit Card</label>
          <div id="card" ref="card" class="input-with-label"></div>
          <div class="cvc-exp-date">
            <b-col class="exp-date">
              <label>Exp Date</label>
              <div id="card" ref="cardExp" class="input-with-label"></div>
            </b-col>
            <b-col class="cvc">
              <label>CVC</label>
              <div id="card" ref="cardCvc" class="input-with-label"></div>
            </b-col>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <label for="couponCode">Coupon Code (optional)</label>
          <div class="coupon-container">
            <BaseInputText
              v-model="couponCode"
              name="couponCode"
              :placeholder="placeholders.couponCode"
              class="left-lock"
            />
            <BaseButton
              type="button"
              @click.stop="checkCouponLocal"
              :disabled="checkingCouponCode"
              class="right-lock btn-secondary"
            >
              <BaseIcon v-if="checkingCouponCode" name="sync" spin />
              <span v-else>
                Apply
              </span>
            </BaseButton>
          </div>
          <p class="err-message" v-if="couponCodeError">
            {{ couponCodeError }}
          </p>
          <p v-if="Object.keys(validCoupon).length > 0">
            {{ validCoupon.name }} Applied Succesfully
          </p>
        </b-col>
      </b-row>
      <BaseButton
        :disabled="submittingPayment || !passThroughQuery.price || !currentUser"
        type="submit"
        class="payment-button btn-secondary  btn-primary"
      >
        <BaseIcon v-if="submittingPayment" name="sync" spin />
        <span
          v-if="
            !submittingPayment &&
              passThroughQuery.price &&
              !_.isEmpty(plan) &&
              plan.recurring &&
              !plan.recurring.trial_period_days &&
              currentUser
          "
        >
          Pay
          {{ plan && !_.isEmpty(plan) ? '$' + plan.unit_amount / 100 : '' }} And
          Get Started
        </span>
        <span
          v-if="
            !submittingPayment &&
              passThroughQuery.price &&
              !_.isEmpty(plan) &&
              plan.recurring &&
              plan.recurring.trial_period_days &&
              currentUser
          "
        >
          Start Trial
        </span>
        <span v-if="!passThroughQuery.price || !currentUser">{{
          `${
            !currentUser && passThroughQuery.price
              ? 'Create your Account First'
              : 'Please Select Plan'
          }`
        }}</span>
      </BaseButton>
      <p
        v-if="
          !_.isEmpty(plan) &&
            plan.recurring.trial_period_days &&
            !plan.metadata.commitment &&
            !plan.metadata.isBootcamp
        "
        >After your trial, you will be charged
        {{
          plan && !_.isEmpty(plan)
            ? '$' + (plan.unit_amount - discountValue(plan)) / 100
            : ''
        }}
        per {{ plan.recurring.interval }}</p
      >
      <p
        v-if="
          !_.isEmpty(plan) &&
            plan.recurring.trial_period_days &&
            !plan.metadata.commitment &&
            plan.metadata.isBootcamp
        "
        >LAUNCHCAMP includes
        {{ Math.round(parseInt(plan.recurring.trial_period_days) / 31) }} months
        free!<br />
        After your trial, you will be charged
        {{
          plan && !_.isEmpty(plan)
            ? '$' + (plan.unit_amount - discountValue(plan)) / 100
            : ''
        }}
        per {{ plan.recurring.interval }}</p
      >
      <p
        v-if="
          !_.isEmpty(plan) &&
            plan.recurring.trial_period_days &&
            plan.metadata.commitment
        "
        >After your trial you will be charged the
        <strong
          >discounted
          {{
            plan && !_.isEmpty(plan)
              ? '$' + (plan.unit_amount - discountValue(plan)) / 100
              : ''
          }}
          per {{ plan.recurring.interval }}</strong
        >, on an annual (12 month) commitment.
      </p>
      <p
        v-if="
          !_.isEmpty(plan) &&
            !plan.recurring.trial_period_days &&
            plan.metadata.commitment
        "
        >Starting today you will be charged the discounted
        {{
          plan && !_.isEmpty(plan)
            ? '$' + (plan.unit_amount - discountValue(plan)) / 100
            : ''
        }}
        per {{ plan.recurring.interval }}, on an annual (12 month) commitment.
      </p>
      <p class="err-message" v-if="authError"> {{ authError }} </p>
    </form>
    <div v-show="plan.unit_amount === 0">
      <p>Your selected plan is Free!</p>
      <BaseButton
        :disabled="submittingPayment || !passThroughQuery.price || !currentUser"
        @click="checkoutFree"
        class="payment-button btn-secondary  btn-primary"
      >
        <BaseIcon v-if="submittingPayment" name="sync" spin />
        <span
          v-if="
            !submittingPayment &&
              passThroughQuery.price &&
              !_.isEmpty(plan) &&
              plan.recurring &&
              !plan.recurring.trial_period_days &&
              currentUser
          "
        >
          Pay
          {{ plan && !_.isEmpty(plan) ? '$' + plan.unit_amount / 100 : '' }} And
          Get Started
        </span>
        <span
          v-if="
            !submittingPayment &&
              passThroughQuery.price &&
              !_.isEmpty(plan) &&
              plan.recurring &&
              plan.recurring.trial_period_days &&
              currentUser
          "
        >
          Start Trial
        </span>
        <span v-if="!passThroughQuery.price || !currentUser">{{
          `${
            !currentUser && passThroughQuery.price
              ? 'Create your Account First'
              : 'Please Select Plan'
          }`
        }}</span>
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.credit-card {
  .cvc-exp-date {
    display: flex;
    justify-content: space-between;
    position: relative;
    width: 100%;
  }
  .cvc {
    padding-left: 2rem;
    padding-right: unset;
  }
  .exp-date {
    padding-left: unset;
    padding-right: unset;
  }
}
.payment-form {
  label {
    text-align: left;
    white-space: nowrap;
  }
}

.col {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  .input-with-label {
    width: 100%;
    ::v-deep .input {
      margin-bottom: 2.2rem !important;
      outline: none;
      &:focus {
        border: 1px solid $color-action;
      }
    }
  }
}

// stylelint-disable no-descending-specificity

.coupon-container {
  align-items: flex-start;
  display: flex;
  justify-content: center;
  justify-items: space-between;
  width: 100%;
  .left-lock {
    flex-grow: 1;
    margin: 0;
    margin-left: 0;
    margin-right: 20px;

    ::v-deep .input {
      margin-bottom: 0;
      outline: none;
      &:focus {
        border: 1px solid $color-action;
      }
    }
  }
  .right-lock {
    margin: 0;
    margin-left: auto;
    margin-right: 0;
  }
  button {
    background-color: transparent;
    border: 2px solid $color-action !important;
    border-radius: 99rem;
  }
}
.payment-button {
  align-items: center;
  background-size: 400% 400%;
  border-radius: 99rem;
  display: flex;
  height: 3rem;
  justify-content: center;
  margin-bottom: 1rem;
  margin-left: auto;
  margin-right: auto;
  margin-top: 5rem;
  padding: 2rem 0;
  width: 55%;
  &:disabled {
    opacity: 0.5;
    &:hover {
      background-color: white;
      color: $color-action;
    }
  }
}

@-webkit-keyframes buttonAnimation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
@-moz-keyframes buttonAnimation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
@keyframes buttonAnimation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.login {
  background-color: $color-off-white-one;
}

.login-footer {
  .terms {
    flex-basis: 50%;
    flex-grow: 2;
    font-size: 1.4rem;
    line-height: 2rem;
    text-align: left;
    a {
      color: $color-action;
      letter-spacing: unset;
      text-decoration: none;
    }
  }
  button {
    border-radius: 2.5rem !important;
    margin-top: 24px;
  }
}

.form-header {
  pre {
    font-family: $base-font-family;
    font-size: 1.2rem;
    font-weight: 800;
    letter-spacing: 0.1px;
    line-height: 2.2rem;
    margin: 0;
    text-align: left;
    text-transform: uppercase;
  }
  h1 {
    font-family: $base-font-family;
    font-size: 4.8rem;
    font-weight: black;
    letter-spacing: -0.76px;
    line-height: 5.2rem;
    margin: 0;
    margin-bottom: 24px;
    text-align: left;
  }
}

.StripeElement {
  background-color: $color-off-white;
  border: 1px solid $color-input-bg;
  border-radius: $base-border-radius;
  display: block;
  line-height: 10vh;
  margin-bottom: 2.5rem;
  max-height: 5rem;
  min-height: 1rem;
  padding: 1.3em 1.8em;
  transition: background $duration-animation-base,
    border $duration-animation-base;
  width: 100%;
}
.StripeElement--focus {
  border: 1px solid $color-action;
}

.err-message {
  color: red;
  margin: 10px;
  padding: 10px;
}
</style>
